"use client";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "./command";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import React from "react";
import { Button } from "./button";
import { selectVariants } from "./select";
import { cn } from "./lib/utils";
import {
  ChevronUpDownIcon,
  XMarkIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";

interface MultiSelectProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "onChange"
> {
  options: {
    label: string;
    value: string;
    icon?: React.ComponentType<{ className?: string }>;
  }[];
  onValueChange: (value: string[]) => void;
  value: string[];
  placeholder?: string;
  label?: string;
  animation?: number;
  maxCount?: number;
  modalPopover?: boolean;
  asChild?: boolean;
  className?: string;
  variant?: "default" | "alternate";
  disabled?: boolean;
  size?: "default" | "sm";
}

export const MultiSelect = React.forwardRef<HTMLDivElement, MultiSelectProps>(
  (
    {
      options,
      onValueChange,
      value = [],
      placeholder = "Select options",
      label = "Stores",
      animation = 0,
      maxCount = 3,
      modalPopover = false,
      asChild = false,
      className,
      variant,
      disabled = false,
      onKeyDown,
      role,
      tabIndex,
      size,
      ...props
    },
    ref
  ) => {
    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
    const toggleOption = (option: string) => {
      if (maxCount === 1) {
        // For single selection, replace the current selection
        const newSelectedValues = value.includes(option) ? [] : [option];
        onValueChange(newSelectedValues);
      } else {
        // For multi-selection, add/remove from array
        const newSelectedValues = value.includes(option)
          ? value.filter((value) => value !== option)
          : [...value, option];
        onValueChange(newSelectedValues);
      }
    };

    const handleRemoveItem = (item: string) => {
      onValueChange(value.filter((value) => value !== item));
    };

    const handleTriggerKeyDown = (
      event: React.KeyboardEvent<HTMLDivElement>
    ) => {
      onKeyDown?.(event);

      if (event.defaultPrevented || disabled) {
        return;
      }

      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        event.currentTarget.click();
      }
    };

    return (
      <Popover
        open={isPopoverOpen}
        onOpenChange={setIsPopoverOpen}
        modal={modalPopover}
      >
        <PopoverTrigger asChild disabled={disabled}>
          <div
            ref={ref}
            {...props}
            role={role ?? "button"}
            tabIndex={disabled ? -1 : (tabIndex ?? 0)}
            aria-disabled={disabled || undefined}
            data-disabled={disabled ? "" : undefined}
            onKeyDown={handleTriggerKeyDown}
            className={cn(
              selectVariants({ variant: variant ?? "default", size }),
              disabled && "pointer-events-none opacity-50",
              size === "sm"
                ? "min-h-8 lg:min-h-9 py-0 h-auto active:ring-transparent"
                : "min-h-10 lg:min-h-12 py-0 h-auto active:ring-transparent",
              className
            )}
          >
            {value.length > 0 ? (
              <div className="flex justify-between items-center flex-1 min-w-0">
                <div className="flex items-center gap-1 overflow-hidden">
                  {value.slice(0, maxCount).map((val) => {
                    const option = options.find((o) => o.value === val);
                    return (
                      <div
                        className={cn(
                          "flex items-center gap-1 rounded-lg text-foreground shrink-0",
                          size === "sm" ? "py-0.5 px-1.5" : "py-1 px-2",
                          variant === "alternate" ? "bg-card" : "bg-background"
                        )}
                        key={val}
                      >
                        <p
                          className={cn(
                            "truncate max-w-[80px] sm:max-w-[120px]",
                            size === "sm" ? "text-xs" : "text-sm"
                          )}
                        >
                          {option?.label ?? ""}
                        </p>
                        <button
                          type="button"
                          aria-label={`Remove ${option?.label ?? value}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleRemoveItem(val);
                          }}
                          className={cn(
                            "text-muted-foreground hover:text-foreground hover:bg-foreground/10 rounded-lg",
                            size === "sm" ? "p-0.5" : "p-1"
                          )}
                        >
                          <XMarkIcon
                            className={size === "sm" ? "h-3 w-3" : "h-4 w-4"}
                          />
                        </button>
                      </div>
                    );
                  })}
                  {value.length > maxCount && (
                    <div
                      className={cn(
                        "flex items-center rounded-lg text-foreground shrink-0",
                        size === "sm"
                          ? "py-0.5 px-1.5 text-xs"
                          : "py-1 px-2 text-sm",
                        variant === "alternate" ? "bg-card" : "bg-background"
                      )}
                    >
                      +{value.length - maxCount} more
                    </div>
                  )}
                </div>

                <ChevronUpDownIcon
                  className={cn(
                    "cursor-pointer ml-2 shrink-0",
                    size === "sm" ? "h-4" : "h-5"
                  )}
                />
              </div>
            ) : (
              <div className="flex items-center justify-between w-full mx-auto">
                <span
                  className={cn(
                    "text-muted-foreground truncate mr-2",
                    size === "sm" ? "text-xs" : "text-xs sm:text-sm"
                  )}
                >
                  {placeholder}
                </span>
                <ChevronUpDownIcon
                  className={cn(
                    "cursor-pointer shrink-0",
                    size === "sm" ? "h-4" : "h-5"
                  )}
                />
              </div>
            )}
          </div>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto min-w-80 p-0"
          align="start"
          onEscapeKeyDown={() => setIsPopoverOpen(false)}
        >
          <Command>
            <div className="flex items-center justify-between mb-1 mt-2 mx-3">
              <p className="font-semibold text-sm">{label}</p>
              <Button
                variant="link"
                size="sm"
                className="hover:no-underline"
                onClick={() =>
                  onValueChange(
                    value.length === options.length
                      ? []
                      : options.map((el) => el.value)
                  )
                }
              >
                {value.length === options.length
                  ? "Deselect All"
                  : "Select All"}
              </Button>
            </div>
            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup>
                {options.map((option) => {
                  const isSelected = value.includes(option.value);
                  return (
                    <CommandItem
                      key={option.value}
                      onSelect={() => toggleOption(option.value)}
                      className="cursor-pointer flex justify-between"
                    >
                      <span>{option.label}</span>
                      <CheckIcon
                        className={cn(
                          "ml-2 h-5 w-5",
                          isSelected ? "opacity-100" : "opacity-0"
                        )}
                      />
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  }
);
MultiSelect.displayName = "MultiSelect";
